/**
 * Single mounting point for the tour overlays. Reads the current step
 * from the OnboardingProvider and renders the right surface (tooltip
 * vs slideout) plus the floating checklist.
 *
 * If a tooltip step's target element isn't mounted yet (user is on a
 * different route), the checklist remains visible — the engine simply
 * waits for the target to register before drawing the beacon + tooltip.
 */
import { OnboardingChecklist } from './OnboardingChecklist';
import { TourBeacon } from './TourBeacon';
import { TourSlideout } from './TourSlideout';
import { TourTooltip } from './TourTooltip';
import { useOnboarding } from './OnboardingContext';
import { useUi } from '@/store/ui.store';

export function TourOrchestrator(): JSX.Element | null {
  const { currentStep, isActive, getTarget } = useOnboarding();
  // Wave 320 — pause the tour while a top-level slide-over is open.
  // Without this the beacon + tooltip (zIndex 9998/9999) bleed through
  // the Prumo backdrop and the user sees ghosts like "Nova obra" pill
  // floating inside the panel content. The tour resumes the moment
  // the dialog closes.
  const prumoOpen = useUi((s) => s.prumoOpen);
  if (prumoOpen) return null;

  const checklist = <OnboardingChecklist />;
  if (!isActive || !currentStep) return checklist;

  if (currentStep.kind === 'slideout') {
    return (
      <>
        {checklist}
        <TourSlideout step={currentStep} />
      </>
    );
  }

  const targetEl = currentStep.targetAttr ? getTarget(currentStep.targetAttr) : null;
  return (
    <>
      {checklist}
      {targetEl && (
        <>
          <TourBeacon targetEl={targetEl} />
          <TourTooltip step={currentStep} targetEl={targetEl} />
        </>
      )}
    </>
  );
}
